import { useQuery } from '@tanstack/react-query';
import { authedFetch } from '../lib/supabase';
import { useAuth } from '../stores/auth';

/**
 * useFeatureFlags
 * ---------------------------------------------------------------------
 * Returns the effective feature-flag map for the current org (or a VOM's
 * viewing org).  Every known key is guaranteed to be present as a boolean
 * so consumers can render deterministic UI.
 *
 * Missing / loading state: returns `{ flags: null, isLoading: true }` so
 * callers can either show a spinner (route-level guard) or optimistically
 * assume enabled (nav rendering).  Super admins on the platform (no org)
 * get an all-enabled synthetic map.
 */
export const FEATURE_KEYS = [
  'patients',
  'schedule',
  'team_management',
  'documents',
  'kpi_analytics',
  'billing',
  'compliance',
  'payroll',
  'show_phi',
];

// Synthetic map for platform-level super admin sessions (no org context).
// Every module is on; PHI display stays fail-closed.
const ALL_ON = { ...Object.fromEntries(FEATURE_KEYS.map((k) => [k, true])), show_phi: false };

export function useFeatureFlags() {
  const { org, profile } = useAuth();
  const orgId = org?.id;
  const isSuperOnPlatform = profile?.role === 'super_admin' && !orgId;

  const q = useQuery({
    queryKey: ['org-features', orgId],
    queryFn: () => authedFetch(`/orgs/${orgId}/features`),
    enabled: !!orgId && !isSuperOnPlatform,
    staleTime: 30 * 1000,
    retry: 1,
  });

  if (isSuperOnPlatform) return { flags: ALL_ON, isLoading: false, isError: false };
  if (!orgId) return { flags: null, isLoading: true, isError: false };

  return {
    flags: q.data?.feature_flags || null,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

/** Predicate helper for one-off checks outside of components. */
export function isFeatureEnabled(flags, key) {
  if (!flags) return true; // fail-open while flags load — the API will still block
  return flags[key] !== false;
}

/**
 * useShowPhi — master PHI display toggle.  FAIL-CLOSED: returns `true`
 * only when the org's `show_phi` flag is explicitly true.  While flags are
 * loading, missing, or errored, PHI stays hidden.
 */
export function useShowPhi() {
  const { flags } = useFeatureFlags();
  return flags?.show_phi === true;
}
